import { useMemo, useState } from "react";
import {
  ArrowRightLeft,
  Coins,
  Download,
  Landmark,
  Receipt,
  ShieldCheck,
  Store,
} from "lucide-react";

import { FLASHPAY_ROLES, useRealmRoles } from "@/core/auth";
import { Alert, AlertDescription, AlertTitle } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { LoadScreen } from "@/core/components/shared/loaders";
import { PageHeader } from "@/core/components/shared/page-header";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { DEFAULT_CURRENCY } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { FinanceCard } from "@/features/shared/components/dashboard/charts/chart-finance";
import { TotalTransactionCard } from "@/features/shared/components/dashboard/charts/chart-total-transaction";
import { StatisticsCard01 } from "@/features/shared/components/dashboard/statistics/statistics-card-01";
import { StatisticsTotalRevenueCard } from "@/features/shared/components/dashboard/statistics/statistics-total-revenue-card";
import { useReportingExport } from "@/features/reporting/api/use-reporting-export";
import { useReportingGauges } from "@/features/reporting/api/use-reporting-gauges";
import { useReportingOverview } from "@/features/reporting/api/use-reporting-overview";
import { useReportingSeries } from "@/features/reporting/api/use-reporting-series";
import { useReportingSummary } from "@/features/reporting/api/use-reporting-summary";
import type { ReportingServiceError, ReportingTotalDto } from "@/features/reporting/api/dto";
import { findTotal, mergeGauges } from "@/features/reporting/api/parse";
import {
  clampReportingRange,
  defaultReportingRange,
} from "@/features/reporting/api/range";
import { ReportingDateRange } from "@/features/reporting/components/reporting-date-range";
import { ReportingGaugesDrawer } from "@/features/reporting/components/reporting-gauges-drawer";
import {
  abandonRate,
  formatCount,
  kpiAmount,
  kycCasesTotal,
  toFinanceChart,
  toRevenueChart,
  toTransactionChart,
} from "@/routes/dashboard/dashboard-mappers";

const OVERVIEW_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;
const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const totalsFrom = (
  overviewTotals: ReportingTotalDto[] | undefined,
  summaryTotals: ReportingTotalDto[] | undefined
): ReportingTotalDto[] => overviewTotals ?? summaryTotals ?? [];

/** Tableau de bord live — `GET /api/v1/Reporting/overview` + séries / jauges. */
export function DashboardPage() {
  const { t } = useTypedTranslation();
  const realmRoles = useRealmRoles();
  const canOverview = OVERVIEW_ROLES.some((role) => realmRoles.includes(role));
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const [range, setRange] = useState(defaultReportingRange);
  const clamped = useMemo(
    () => clampReportingRange(range.from, range.to),
    [range]
  );

  const overview = useReportingOverview(clamped, canOverview);
  const walletSummary = useReportingSummary("wallet", clamped, canOverview);
  const kycSummary = useReportingSummary("kyc", clamped, canOverview);
  const ledgerSummary = useReportingSummary("ledger", clamped, canOverview);
  const merchantSummary = useReportingSummary("merchant", clamped, canOverview);
  const feeSummary = useReportingSummary("fee-engine", clamped, canOverview);

  const txSeries = useReportingSeries(
    "wallet",
    { ...clamped, metricKey: "transaction.count", currency: DEFAULT_CURRENCY },
    canOverview
  );
  const feeCollectedSeries = useReportingSeries(
    "wallet",
    { ...clamped, metricKey: "transaction.fee", currency: DEFAULT_CURRENCY },
    canOverview
  );
  const feeComputedSeries = useReportingSeries(
    "fee-engine",
    { ...clamped, metricKey: "fee.computed", currency: DEFAULT_CURRENCY },
    canOverview
  );
  const saleNetSeries = useReportingSeries(
    "merchant",
    { ...clamped, metricKey: "merchant.sale_net", currency: DEFAULT_CURRENCY },
    canOverview
  );
  const saleGrossSeries = useReportingSeries(
    "merchant",
    { ...clamped, metricKey: "merchant.sale_gross", currency: DEFAULT_CURRENCY },
    canOverview
  );
  const saleFeeSeries = useReportingSeries(
    "merchant",
    { ...clamped, metricKey: "merchant.sale_fee", currency: DEFAULT_CURRENCY },
    canOverview
  );

  const walletGauges = useReportingGauges("wallet", canGauges);
  const kycGauges = useReportingGauges("kyc", canGauges);
  const ledgerGauges = useReportingGauges("ledger", canGauges);
  const merchantGauges = useReportingGauges("merchant", canGauges);
  const feeGauges = useReportingGauges("fee-engine", canGauges);

  const csvExport = useReportingExport();

  const errors = useMemo(() => {
    const list: ReportingServiceError[] = [...(overview.data?.errors ?? [])];
    const failed: [string, { message?: string } | null][] = [
      ["wallet", walletSummary.error],
      ["kyc", kycSummary.error],
      ["ledger", ledgerSummary.error],
      ["merchant", merchantSummary.error],
      ["fee-engine", feeSummary.error],
    ];
    for (const [service, error] of failed) {
      if (!error) continue;
      if (list.some((entry) => entry.service === service)) continue;
      list.push({ service, message: error.message ?? t("common.error") });
    }
    return list;
  }, [
    overview.data?.errors,
    walletSummary.error,
    kycSummary.error,
    ledgerSummary.error,
    merchantSummary.error,
    feeSummary.error,
    t,
  ]);

  const walletTotals = totalsFrom(
    overview.data?.services.wallet?.totals,
    walletSummary.data?.totals
  );
  const kycTotals = totalsFrom(
    overview.data?.services.kyc?.totals,
    kycSummary.data?.totals
  );
  const ledgerTotals = totalsFrom(
    overview.data?.services.ledger?.totals,
    ledgerSummary.data?.totals
  );
  const merchantTotals = totalsFrom(
    overview.data?.services.merchant?.totals,
    merchantSummary.data?.totals
  );
  const feeTotals = totalsFrom(
    overview.data?.services["fee-engine"]?.totals ??
      overview.data?.services.fee?.totals,
    feeSummary.data?.totals
  );

  const gauges = mergeGauges(
    walletGauges.data,
    kycGauges.data,
    ledgerGauges.data,
    merchantGauges.data,
    feeGauges.data,
    overview.data?.services.wallet?.gauges,
    overview.data?.services.kyc?.gauges,
    overview.data?.services.ledger?.gauges,
    overview.data?.services.merchant?.gauges,
    overview.data?.services["fee-engine"]?.gauges,
    walletSummary.data?.gauges,
    kycSummary.data?.gauges,
    ledgerSummary.data?.gauges,
    merchantSummary.data?.gauges,
    feeSummary.data?.gauges
  );

  const txCount = findTotal(walletTotals, "transaction.count");
  const txFee = findTotal(walletTotals, "transaction.fee", DEFAULT_CURRENCY);
  const txFailure = findTotal(walletTotals, "transaction.failure");
  const kycCases = kycCasesTotal(kycTotals);
  const saleNet = findTotal(merchantTotals, "merchant.sale_net", DEFAULT_CURRENCY);
  const saleGross = findTotal(merchantTotals, "merchant.sale_gross", DEFAULT_CURRENCY);
  const saleFee = findTotal(merchantTotals, "merchant.sale_fee", DEFAULT_CURRENCY);
  const posted = findTotal(ledgerTotals, "ledger.posted_amount", DEFAULT_CURRENCY);
  const computed = findTotal(feeTotals, "fee.computed", DEFAULT_CURRENCY);
  const abandon = abandonRate(computed, txFee);

  const initialLoading =
    canOverview &&
    overview.isLoading &&
    walletSummary.isLoading &&
    !overview.data &&
    !walletSummary.data;

  const handleExport = (service: "wallet" | "merchant") => {
    csvExport.mutate(
      {
        service,
        ...clamped,
        metricKey:
          service === "wallet" ? "transaction.count" : "merchant.sale_net",
        currency: DEFAULT_CURRENCY,
      },
      {
        onSuccess: () => notifSilence(t("dashboard.export.success")),
        onError: (error) =>
          notifSilenceError(
            error instanceof Error ? error.message : t("dashboard.export.error")
          ),
      }
    );
  };

  if (initialLoading) return <LoadScreen />;

  return (
    <section className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title={t("layout.home.title")}
        titleClassName="text-gradient-accent"
        description={t("layout.home.description")}
        actions={
          canGauges ? <ReportingGaugesDrawer gauges={gauges} /> : null
        }
      />

      {canOverview ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <ReportingDateRange value={range} onChange={setRange} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="border-accent/30 hover:border-accent"
              disabled={csvExport.isPending}
              onClick={() => handleExport("wallet")}
            >
              {csvExport.isPending ? <ButtonSpinner /> : <Download className="mr-2 size-4" />}
              {t("dashboard.export.wallet")}
            </Button>
            <Button
              variant="outline"
              className="border-accent/30 hover:border-accent"
              disabled={csvExport.isPending}
              onClick={() => handleExport("merchant")}
            >
              {csvExport.isPending ? <ButtonSpinner /> : <Download className="mr-2 size-4" />}
              {t("dashboard.export.merchant")}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("dashboard.serviceOnly")}</p>
      )}

      {errors.length > 0 ? (
        <Alert variant="destructive" className="bg-destructive/10">
          <AlertTitle>{t("dashboard.errors.title")}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {errors.map((error) => (
                <li key={`${error.service}-${error.message}`}>
                  {t("dashboard.errors.serviceUnreachable", {
                    service: error.service,
                    message: error.message,
                  })}
                </li>
              ))}
            </ul>
            <p className="mt-2">{t("dashboard.partialHint")}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {canOverview ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <StatisticsCard01
              icon={<ArrowRightLeft className="size-4" />}
              value={formatCount(txCount?.count)}
              title={t("dashboard.kpi.transactionCount")}
              changePercentage={t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.period")}
            />
            <StatisticsCard01
              icon={<Receipt className="size-4" />}
              value={kpiAmount(txFee)}
              title={t("dashboard.kpi.transactionFee")}
              changePercentage={abandon ?? t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.abandon")}
            />
            <StatisticsCard01
              icon={<ShieldCheck className="size-4" />}
              value={formatCount(kycCases)}
              title={t("dashboard.kpi.kycCases")}
              changePercentage={t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.period")}
            />
            <StatisticsCard01
              icon={<Store className="size-4" />}
              value={kpiAmount(saleNet)}
              title={t("dashboard.kpi.merchantSaleNet")}
              changePercentage={t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.period")}
            />
            <StatisticsCard01
              icon={<Landmark className="size-4" />}
              value={kpiAmount(posted)}
              title={t("dashboard.kpi.ledgerPosted")}
              changePercentage={t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.period")}
            />
            <StatisticsCard01
              icon={<Coins className="size-4" />}
              value={kpiAmount(computed)}
              title={t("dashboard.kpi.feeComputed")}
              changePercentage={abandon ?? t("dashboard.kpi.noData")}
              comparisonLabel={t("dashboard.kpi.abandon")}
            />
          </div>

          <TotalTransactionCard
            title={t("dashboard.charts.transactions.title")}
            subTitle={t("dashboard.charts.transactions.subTitle")}
            reportTitle={t("dashboard.charts.transactions.reportTitle")}
            reportSubTitle={t("dashboard.charts.transactions.reportSubTitle")}
            chartData={toTransactionChart(txSeries.data ?? [])}
            stats={[
              {
                icon: <ArrowRightLeft className="size-4" />,
                label: t("dashboard.charts.transactions.count"),
                value: formatCount(txCount?.count),
              },
              {
                icon: <Receipt className="size-4" />,
                label: t("dashboard.charts.transactions.fee"),
                value: kpiAmount(txFee),
              },
              {
                icon: <Coins className="size-4" />,
                label: t("dashboard.charts.transactions.failure"),
                value: formatCount(txFailure?.count),
              },
            ]}
            performanceLabel={t("dashboard.charts.transactions.performance")}
            performanceValue={formatCount(txCount?.count)}
            menuAriaLabel={t("dashboard.menu.aria")}
          />

          <StatisticsTotalRevenueCard
            title={kpiAmount(txFee)}
            description={t("dashboard.charts.revenue.description")}
            changePercentage={abandon ?? t("dashboard.kpi.noData")}
            chartData={toRevenueChart(
              feeComputedSeries.data ?? [],
              feeCollectedSeries.data ?? []
            )}
            primaryLabel={t("dashboard.charts.revenue.computed")}
            secondaryLabel={t("dashboard.charts.revenue.collected")}
          />

          <FinanceCard
            title={t("dashboard.charts.finance.title")}
            subTitle={t("dashboard.charts.finance.subTitle")}
            reportTitle={t("dashboard.charts.finance.reportTitle")}
            reportSubTitle={t("dashboard.charts.finance.reportSubTitle")}
            chartData={toFinanceChart(
              saleNetSeries.data ?? [],
              saleGrossSeries.data ?? [],
              saleFeeSeries.data ?? []
            )}
            reportData={[
              {
                icon: <Store className="size-4" />,
                title: t("dashboard.charts.finance.income"),
                amount: kpiAmount(saleGross),
              },
              {
                icon: <Receipt className="size-4" />,
                title: t("dashboard.charts.finance.expense"),
                amount: kpiAmount(saleFee),
              },
              {
                icon: <Coins className="size-4" />,
                title: t("dashboard.charts.finance.profit"),
                amount: kpiAmount(saleNet),
              },
            ]}
            labels={{
              profit: t("dashboard.charts.finance.profit"),
              income: t("dashboard.charts.finance.income"),
              expense: t("dashboard.charts.finance.expense"),
            }}
            menuAriaLabel={t("dashboard.menu.aria")}
          />
        </>
      ) : null}
    </section>
  );
}
